import { useEffect, useRef, useState } from "react";
import { Form, Input, Select, InputNumber, Row, Col, Card, App, Button, Space, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useSelect } from "@refinedev/antd";
import { useCustomMutation, useOne } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router";
import { usePageTitle } from "../../hooks";
import { MapWithDraw } from "./components/MapWithDraw";
import { getProvinceCenterByName } from "./utils/province-centers";

// PR3 — Igual que en farm-create: la variedad vive ahora en cada
// parcela declarada y se edita desde el MapWithDraw, no a nivel
// finca. El selector y el form.item se eliminan.

export const FarmEdit = () => {
  const { t } = useTranslation();
  const { id: producerId, farmId } = useParams<{ id: string; farmId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const { mutate } = useCustomMutation();
  const [formReady, setFormReady] = useState(false);
  usePageTitle(t("producers.farms.actions.edit", "Editar Finca"));

  const referencePlotsRef = useRef<
    Array<{
      id?: number;
      geom: GeoJSON.Geometry;
      surface: number;
      estimated_planting_date?: string;
      poligono?: number | null;
      parcela?: number | null;
      recinto?: number | null;
      // PR3 — Variedad obligatoria por parcela.
      variety?: number | null;
    }>
  >([]);
  // True cuando el usuario ha modificado el conjunto de parcelas del
  // mapa (draw / edit / delete / KML / cambio de superficie o variedad).
  // Sin este flag, un save "inocente" (usuario solo cambia el nombre
  // de la finca) enviaba `reference_plots: []` porque la ref aún no
  // estaba poblada por MapWithDraw → el backend borraba TODAS las
  // parcelas. Con el flag, solo enviamos reference_plots cuando el
  // usuario realmente ha tocado el mapa.
  const mapDirtyRef = useRef(false);

  // Fetch producer detail to get the full farm data (with reference_plots, variety, etc.)
  const { query: producerQuery } = useOne<any>({
    resource: "producers",
    id: producerId!,
  });

  const producerData = producerQuery?.data;
  const isLoading = producerQuery?.isLoading;
  const farm = producerData?.data?.farms?.find((f: any) => String(f.id) === farmId);

  // Populate form when farm data is available
  useEffect(() => {
    if (farm && !formReady) {
      form.setFieldsValue({
        name: farm.name,
        country: farm.country,
        province: farm.province,
        town: farm.town,
        surface_tango: farm.surface_tango,
        total_surface: farm.total_surface,
        production_type: farm.production_type,
      });
      setFormReady(true);
    }
  }, [farm, form, formReady]);

  const { selectProps: countrySelectProps } = useSelect({
    resource: "countries",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const selectedCountry = Form.useWatch("country", form);
  const selectedProvince = Form.useWatch("province", form);

  const { selectProps: provinceSelectProps } = useSelect({
    resource: "v1/provinces",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
    filters: selectedCountry
      ? [{ field: "country", operator: "eq", value: selectedCountry }]
      : [],
    queryOptions: { enabled: !!selectedCountry },
  });

  // Compute plots with valid geometry and default map center from province
  const plotsWithGeom = farm?.reference_plots?.filter((p: any) => p.geom != null) ?? [];
  const provinceName = provinceSelectProps.options?.find(
    (o: any) => o.value === farm?.province,
  )?.label as string | undefined;
  const defaultMapCenter = provinceName ? getProvinceCenterByName(provinceName) : undefined;

  const { selectProps: townSelectProps } = useSelect({
    resource: "v1/towns",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 2000 },
    filters: selectedProvince
      ? [{ field: "province", operator: "eq", value: selectedProvince }]
      : [],
    queryOptions: { enabled: !!selectedProvince },
  });

  // Clear province + town when country changes, clear town when province changes
  const prevCountryRef = useRef(selectedCountry);
  const prevProvinceRef = useRef(selectedProvince);
  useEffect(() => {
    if (prevCountryRef.current !== undefined && prevCountryRef.current !== selectedCountry) {
      form.setFieldsValue({ province: undefined, town: undefined });
    }
    prevCountryRef.current = selectedCountry;
  }, [selectedCountry, form]);
  useEffect(() => {
    if (prevProvinceRef.current !== undefined && prevProvinceRef.current !== selectedProvince) {
      form.setFieldValue("town", undefined);
    }
    prevProvinceRef.current = selectedProvince;
  }, [selectedProvince, form]);

  const selectFilter = (input: string, option: any) =>
    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  // Plots without geometry — preserve them on save
  const plotsWithoutGeom = farm?.reference_plots?.filter((p: any) => p.geom == null) ?? [];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // PR3 — Validación cliente: cada parcela debe traer variedad
      // (incluidas las preservadas sin geometría). Solo cuando el
      // usuario ha modificado el mapa — en un save que no toca
      // reference_plots (mapDirtyRef=false) no tiene sentido validar
      // parcelas que no vamos a mandar.
      if (mapDirtyRef.current) {
        const allPlots = [...referencePlotsRef.current, ...plotsWithoutGeom];
        const plotsMissingVariety = allPlots.filter(
          (p: any) => p.variety == null && p.variety_id == null,
        );
        if (plotsMissingVariety.length > 0) {
          setSubmitting(false);
          message.error(
            t(
              "producers.farms.messages.plot_variety_required",
              "Cada parcela debe tener una variedad protegida asignada ({{n}} sin asignar).",
              { n: plotsMissingVariety.length },
            ),
          );
          return;
        }
      }

      // Merge map-drawn plots with existing plots that have no geometry
      const drawnPlots = referencePlotsRef.current.map((p) => ({
        ...(p.id ? { id: p.id } : {}),
        geom: p.geom,
        surface: p.surface,
        estimated_planting_date: p.estimated_planting_date || null,
        poligono: p.poligono ?? null,
        parcela: p.parcela ?? null,
        recinto: p.recinto ?? null,
        variety: p.variety ?? null,
      }));
      const preservedPlots = plotsWithoutGeom.map((p: any) => ({
        id: p.id,
        geom: null,
        surface: p.surface ?? 0,
        estimated_planting_date: p.estimated_planting_date || null,
        poligono: p.poligono ?? null,
        parcela: p.parcela ?? null,
        recinto: p.recinto ?? null,
        variety: p.variety_id ?? p.variety ?? null,
      }));

      // GUARD contra el full-delete accidental de plots. El backend
      // `FarmCreateSerializer.update` hace
      //   plot_set.exclude(id__in=incoming_ids).delete()
      // en cuanto reciba `reference_plots` no-nulo — con una lista
      // vacía borra TODO. Solo enviamos reference_plots cuando el
      // usuario haya modificado explícitamente el mapa (draw / edit /
      // delete / KML / cambio de campo). En un save "inocente"
      // (cambia solo nombre/país/etc.) omitimos la clave y el backend
      // deja las parcelas intactas.
      const payload: Record<string, any> = { ...values };
      if (mapDirtyRef.current) {
        payload.reference_plots = [...drawnPlots, ...preservedPlots];
      }

      mutate(
        {
          url: `/v1/farms/${farmId}/`,
          method: "patch",
          values: payload,
        },
        {
          onSuccess: () => {
            setSubmitting(false);
            message.success(t("producers.farms.messages.updated_success", "Finca actualizada correctamente"));
            navigate(backUrl);
          },
          onError: () => {
            setSubmitting(false);
          },
        },
      );
    } catch {
      // validation errors shown inline
    }
  };

  const fromState = (location.state as any)?.from;
  const fromFarmId = (location.state as any)?.farmId;
  const backUrl = fromState === "farms"
    ? "/farms"
    : fromState === "farm-show" && fromFarmId
      ? `/farms/show/${fromFarmId}`
      : `/producers/show/${producerId}`;

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={t("producers.farms.actions.edit", "Editar Finca")}
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backUrl)}>
          {t("common.back", "Volver")}
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={t("producers.farms.fields.name", "Nombre")}
              name="name"
              rules={[{ required: true, message: t("producers.farms.messages.name_required", "El nombre de la finca es obligatorio") }]}
            >
              <Input placeholder={t("producers.farms.placeholders.name", "Nombre de la finca")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.country", "País")} name="country">
              <Select {...countrySelectProps} placeholder={t("common.select", "Seleccionar")} allowClear showSearch filterOption={selectFilter} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.province", "Provincia")} name="province">
              <Select
                {...provinceSelectProps}
                placeholder={selectedCountry ? t("common.select", "Seleccionar") : t("producers.farms.placeholders.select_country_first", "Seleccione primero un país")}
                allowClear
                showSearch
                filterOption={selectFilter}
                disabled={!selectedCountry}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.town", "Municipio")} name="town">
              <Select
                {...townSelectProps}
                placeholder={selectedProvince ? t("common.select", "Seleccionar") : t("producers.farms.placeholders.select_province_first", "Seleccione primero una provincia")}
                allowClear
                showSearch
                filterOption={selectFilter}
                disabled={!selectedProvince}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            {/* PR3 — Selector de variedad eliminado del form de finca. */}
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.surface_tango", "Superficie Tango")} name="surface_tango">
              <InputNumber style={{ width: "100%" }} min={0} step={0.01} decimalSeparator="," placeholder={t("producers.farms.placeholders.surface_tango", "Ej: 15,50")} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t("producers.farms.reference_plots", "Parcelas de Referencia")}>
          {farm && (
            <div style={{ position: "relative", zIndex: 0 }}>
            <MapWithDraw
              initialPlots={plotsWithGeom.map((p: any) => ({
                id: p.id,
                geom: p.geom,
                surface: p.surface,
                estimated_planting_date: p.estimated_planting_date ?? undefined,
                poligono: p.poligono ?? undefined,
                parcela: p.parcela ?? undefined,
                recinto: p.recinto ?? undefined,
                // PR3 — `variety_id` viene en el payload del serializer.
                variety: p.variety_id ?? p.variety ?? null,
              }))}
              onPlotsChange={(plots) => { referencePlotsRef.current = plots; }}
              onDirty={() => { mapDirtyRef.current = true; }}
              height="600px"
              defaultCenter={defaultMapCenter}
              farmInfo={{
                name: farm.name,
                province_name: farm.province_name,
                town_name: farm.town_name,
                surface_tango: farm.surface_tango,
                total_surface: farm.total_surface,
                variety_display: farm.variety_display,
                production_type: farm.production_type,
              }}
            />
            </div>
          )}
        </Form.Item>
      </Form>

      <div style={{ textAlign: "right", marginTop: 16 }}>
        <Space>
          <Button onClick={() => navigate(backUrl)}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      </div>
    </Card>
  );
};